import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import StatCard from '../components/StatCard';
import KbjuCard from '../components/KbjuCard';
import BodyRadarChart from '../components/BodyRadarChart';
import WeightChart from '../components/WeightChart';
import MeasurementForm from '../components/MeasurementForm';
import MeasurementTable from '../components/MeasurementTable';
import AdminDashboard from './AdminDashboard';
import ClientsTable from '../components/ClientsTable';
import { ChevronLeft, Download, UserX, UserCheck, Save, Plus, LayoutDashboard, Users, Table2 } from 'lucide-react';
import { GOAL_LABELS, MEASUREMENT_LABELS, getUnit } from '../utils/dynamics';

function ClientCard({ client, onSelect }) {
  const name = [client.first_name, client.last_name].filter(Boolean).join(' ') || client.username;
  return (
    <div className="card p-4 cursor-pointer card-hover" onClick={() => onSelect(client.id)}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-fuchsia/10 flex items-center justify-center shrink-0">
          <span className="text-fuchsia font-bold text-sm">{name[0]?.toUpperCase()}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-sm text-ink-light dark:text-ink-dark truncate">{name}</p>
            {!client.is_active && (
              <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded shrink-0">заблок.</span>
            )}
          </div>
          <p className="text-xs text-ink-light-muted dark:text-ink-muted">@{client.username} · {GOAL_LABELS[client.goal] || 'Цель не указана'}</p>
          <div className="flex gap-3 mt-1">
            <span className="text-xs text-ink-light-muted dark:text-ink-muted">{client.measurement_count} замер.</span>
            {client.last_weight && <span className="text-xs text-ink-light-muted dark:text-ink-muted">Вес: {parseFloat(client.last_weight)} кг</span>}
            {client.last_measurement_date && (
              <span className="text-xs text-ink-light-muted dark:text-ink-muted">
                {new Date(client.last_measurement_date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { isSuperAdmin } = useAuth();
  const { clientId }     = useParams();
  const navigate         = useNavigate();

  const [clients,    setClients]    = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [detailLoad, setDetailLoad] = useState(false);
  const [notes,      setNotes]      = useState('');
  const [notesSaved, setNotesSaved] = useState(false);
  const [addingM,    setAddingM]    = useState(false);
  const [savingM,    setSavingM]    = useState(false);
  const [view,       setView]       = useState('dashboard'); // 'dashboard' | 'clients'

  useEffect(() => {
    api.get('/admin/users')
      .then(r => setClients(r.data.users || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (clientId) openClient(clientId);
  }, [clientId]);

  const openClient = async (id) => {
    setDetailLoad(true);
    try {
      const { data } = await api.get(`/admin/users/${id}`);
      setSelected(data);
      setNotes(data.user.trainer_notes || '');
    } finally {
      setDetailLoad(false);
    }
  };

  const saveNotes = async () => {
    await api.put(`/admin/users/${selected.user.id}/notes`, { trainer_notes: notes });
    setNotesSaved(true);
    setTimeout(() => setNotesSaved(false), 2000);
  };

  const [resetPwd,  setResetPwd]  = useState('');
  const [resetMsg,  setResetMsg]  = useState('');
  const [showReset, setShowReset] = useState(false);

  const doResetPassword = async () => {
    if (!resetPwd || resetPwd.length < 6) return setResetMsg('Минимум 6 символов');
    try {
      await api.put(`/admin/users/${selected.user.id}/password`, { password: resetPwd });
      setResetMsg('✅ Пароль изменён');
      setTimeout(() => { setShowReset(false); setResetMsg(''); setResetPwd(''); }, 2000);
    } catch(e) { setResetMsg('Ошибка: ' + (e.response?.data?.error || e.message)); }
  };

  const toggleActive = async () => {
    const { data } = await api.put(`/admin/users/${selected.user.id}/toggle-active`);
    setSelected(prev => ({ ...prev, user: { ...prev.user, is_active: data.is_active } }));
    setClients(prev => prev.map(c => c.id === selected.user.id ? { ...c, is_active: data.is_active } : c));
  };

  const addMeasurement = async (values) => {
    setSavingM(true);
    try {
      await api.post(`/admin/measurements/${selected.user.id}`, values);
      await openClient(selected.user.id);
      setAddingM(false);
    } finally {
      setSavingM(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-8 h-8 rounded-full border-2 border-fuchsia border-t-transparent animate-spin" /></div>;

  // Детальная карточка клиента
  if (selected) {
    const u    = selected.user;
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username;
    const latest   = selected.measurements?.[0];
    const previous = selected.measurements?.[1];

    return (
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <button onClick={() => setSelected(null)} className="btn-ghost flex items-center gap-1.5">
            <ChevronLeft size={16} /> Все клиенты
          </button>
          <div className="flex gap-2">
            <button onClick={async () => {
                const token = localStorage.getItem('fitapp-token');
                const r = await fetch(`/api/admin/export/${u.id}`, { headers:{ Authorization:`Bearer ${token}` } });
                if (!r.ok) { alert('Ошибка выгрузки'); return; }
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href=url; a.download=`client_${u.id}.xlsx`; a.click();
              }} className="btn-secondary flex items-center gap-1.5 text-sm">
              <Download size={14} /> Excel
            </button>
            <button onClick={()=>setShowReset(true)} className="btn-secondary flex items-center gap-1.5 text-sm">
              🔑 Пароль
            </button>
            <button onClick={toggleActive} className={`btn-secondary flex items-center gap-1.5 text-sm ${!u.is_active ? 'text-green-600' : 'text-red-500'}`}>
              {u.is_active ? <><UserX size={14} /> Заблок.</> : <><UserCheck size={14} /> Активировать</>}
            </button>
          </div>
        </div>

        <div className="card p-4 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-fuchsia/10 flex items-center justify-center shrink-0">
            <span className="text-fuchsia font-extrabold text-xl">{name[0]}</span>
          </div>
          <div>
            <p className="font-display font-bold text-lg text-ink-light dark:text-ink-dark">{name}</p>
            <p className="text-sm text-ink-light-muted dark:text-ink-muted">@{u.username} · {GOAL_LABELS[u.goal] || 'Цель не указана'}</p>
            {u.height_cm && <p className="text-xs text-ink-light-muted dark:text-ink-muted">Рост: {u.height_cm} см · {u.gender === 'female' ? '👩 Женский' : u.gender === 'male' ? '👨 Мужской' : ''}</p>}
            {u.telegram_username && (
              <a href={`https://t.me/${u.telegram_username}`} target="_blank" rel="noreferrer"
                className="text-xs text-cyan-app hover:underline inline-flex items-center gap-1 mt-0.5">
                💬 @{u.telegram_username}
              </a>
            )}
          </div>
        </div>

        {latest && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {['weight_kg','waist_cm','abdomen_cm','hips_cm'].map(f => {
              const dyn = latest.dynamics?.find(d => d.field === f);
              return <StatCard key={f} label={MEASUREMENT_LABELS[f]} value={latest[f] ? parseFloat(latest[f]) : null} unit={getUnit(f)} delta={dyn?.delta} status={dyn?.status} />;
            })}
          </div>
        )}

        {selected.kbju && <KbjuCard kbju={selected.kbju} />}
        {latest && <BodyRadarChart current={latest} previous={previous} />}
        {selected.measurements?.length >= 2 && (
          <WeightChart data={selected.measurements} field="weight_kg" label="Вес" color="#E8006F" />
        )}

        {/* Reset password modal */}
        {showReset && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',backdropFilter:'blur(4px)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}}>
            <div className="card" style={{maxWidth:340,width:'100%',padding:'1.5rem'}}>
              <h3 className="font-semibold text-base text-ink-light dark:text-ink-dark mb-1">Сменить пароль</h3>
              <p className="text-xs text-ink-light-muted dark:text-ink-muted mb-4">{name}</p>
              <input type="text" placeholder="Новый пароль (мин. 6 символов)"
                value={resetPwd} onChange={e=>setResetPwd(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-surface-700 dark:bg-surface-800 border border-surface-600 text-sm text-ink-light dark:text-ink-dark mb-3 outline-none"/>
              {resetMsg && <p className="text-xs mb-3" style={{color:resetMsg.startsWith('✅')?'#22C55E':'#EF4444'}}>{resetMsg}</p>}
              <div className="flex gap-2">
                <button onClick={doResetPassword} className="btn-primary flex-1 text-sm">Сохранить</button>
                <button onClick={()=>{setShowReset(false);setResetPwd('');setResetMsg('');}} className="btn-secondary flex-1 text-sm">Отмена</button>
              </div>
            </div>
          </div>
        )}

        {/* История замеров */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="section-label">История ({selected.measurements?.length} замеров)</p>
            <button onClick={() => setAddingM(true)} className="btn-secondary flex items-center gap-1.5 text-sm">
              <Plus size={14} /> Добавить
            </button>
          </div>
          {addingM && (
            <div className="card p-4 mb-4">
              <p className="font-semibold text-sm mb-4 text-ink-light dark:text-ink-dark">Новый замер для {name}</p>
              <MeasurementForm onSubmit={addMeasurement} loading={savingM} onCancel={() => setAddingM(false)} />
            </div>
          )}
          <MeasurementTable measurements={selected.measurements || []} />
        </div>

        {/* Заметки тренера */}
        <div>
          <p className="section-label">Заметки тренера</p>
          <div className="card p-4 space-y-3">
            <textarea rows={4} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Особенности работы с клиентом, противопоказания, прогресс..." className="input resize-none" />
            <button onClick={saveNotes} className="btn-primary flex items-center gap-2">
              <Save size={15} /> {notesSaved ? '✓ Сохранено' : 'Сохранить заметки'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Главный вид — таб «Обзор» и «Клиенты»
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Табы */}
      <div className="flex gap-2">
        <button onClick={() => setView('dashboard')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
            view === 'dashboard' ? 'bg-fuchsia text-white' : 'btn-ghost'
          }`}>
          <LayoutDashboard size={15} /> Обзор
        </button>
        <button onClick={() => setView('clients')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
            view === 'clients' ? 'bg-fuchsia text-white' : 'btn-ghost'
          }`}>
          <Users size={15} /> Клиенты ({clients.length})
        </button>
        <button onClick={() => setView('table')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
            view === 'table' ? 'bg-fuchsia text-white' : 'btn-ghost'
          }`}>
          <Table2 size={15} /> Таблица
        </button>
      </div>

      {view === 'dashboard' && <AdminDashboard clients={clients} onSelect={openClient} />}

      {view === 'table' && <ClientsTable clients={clients} onSelect={openClient} />}

      {view === 'clients' && clients.length > 0 && (
        <button onClick={async () => {
            const token = localStorage.getItem('fitapp-token');
            const r = await fetch('/api/admin/export-all', { headers:{ Authorization:`Bearer ${token}` } });
            if (!r.ok) { alert('Ошибка выгрузки'); return; }
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href=url; a.download='molshape_all_clients.xlsx'; a.click();
          }} className="btn-secondary flex items-center gap-1.5 text-sm">
          <Download size={14} /> Excel (все клиенты)
        </button>
      )}

      {view === 'clients' && (
        clients.length === 0 ? (
          <div className="card p-8 text-center">
            <p className="text-3xl mb-3">👥</p>
            <p className="text-sm text-ink-light-muted dark:text-ink-muted">Клиентов пока нет</p>
          </div>
        ) : (
          clients.map(c => <ClientCard key={c.id} client={c} onSelect={openClient} />)
        )
      )}
    </div>
  );
}
